<!-- Пробег ФАКТа: выбор телеметрии или ручного значения и сравнение с ПЛАНом. -->
<template>
  <section class="fact-mileage">
    <div class="fact-mileage__header">
      <strong>Пробег</strong>
      <span class="fact-mileage__note" :class="{ 'fact-mileage__note--missing': !hasTelemetry && !overridden }">
        {{ sourceNote }}
      </span>

      <span v-if="manualMode && editable" class="fact-mileage__field">
        <PlanCalculationValueInput
          ref="input"
          :model-value="draft"
          :changed="overridden"
          compact
          placeholder="0"
          aria-label="Пробег вручную, км"
          @update:model-value="draft = $event"
          @keydown.esc.stop.prevent="cancelEdit"
          @keydown.enter.prevent="save"
        />
        <span>км</span>
        <PlanCalculationActionButton
          v-if="showSaveAction"
          tone="plan"
          size="dense"
          :disabled="invalid || isSubmitting"
          @click="save"
        >
          {{ isSubmitting ? "Сохраняем…" : "Сохранить" }}
        </PlanCalculationActionButton>
        <PlanCalculationInlineActionButton
          v-if="hasTelemetry && !overridden"
          :disabled="isSubmitting"
          @click="cancelEdit"
        >
          Отмена
        </PlanCalculationInlineActionButton>
        <PlanCalculationInlineActionButton
          v-if="overridden"
          dotted
          title="Удалить ручное значение"
          :disabled="isSubmitting"
          @click="$emit('reset')"
        >
          сбросить
        </PlanCalculationInlineActionButton>
      </span>

      <span class="fact-mileage__freshness">
        <span>{{ telemetryStatus }}</span>
        <PlanCalculationRefreshTelemetryButton
          v-if="editable"
          :disabled="isSubmitting || refreshPending"
          @click="$emit('refresh')"
        />
      </span>

      <BaseSegmentedControl
        v-if="editable"
        v-model="selectedSource"
        class="fact-mileage__source"
        :options="sourceOptions"
        :disabled="isSubmitting"
        @change="changeSource"
      />
    </div>

    <div class="fact-mileage__comparison">
      <div class="fact-mileage__bar" aria-hidden="true">
        <span
          class="fact-mileage__fill"
          :class="{ 'fact-mileage__fill--manual': overridden }"
          :style="{ width: `${comparison.actualPercent}%` }"
        ></span>
        <span
          v-if="hasPlanMileage"
          class="fact-mileage__plan-marker"
          :style="{ left: `${comparison.planPercent}%` }"
        ></span>
      </div>
      <span class="fact-mileage__comparison-label" :class="differenceTone">
        {{ comparisonLabel }}
      </span>
    </div>
  </section>
</template>

<script>
import PlanCalculationActionButton from "@/components/Order/PlanCalculation/Buttons/Action.vue";
import PlanCalculationInlineActionButton from "@/components/Order/PlanCalculation/Buttons/Inline.vue";
import PlanCalculationRefreshTelemetryButton from "@/components/Order/PlanCalculation/Buttons/RefreshTelemetry.vue";
import PlanCalculationValueInput from "@/components/Order/PlanCalculation/Inputs/Value.vue";
import { formatBigNumber } from "@/utils/numberUtils";
import {
  buildMileageComparison,
  buildTelemetryFreshnessLabel,
  isMileageDraftChanged,
} from "@/utils/orderCalculation/factPresentationUtils";

export default {
  name: "OrderPlanCalculationFactMileageEditor",

  components: {
    PlanCalculationActionButton,
    PlanCalculationInlineActionButton,
    PlanCalculationRefreshTelemetryButton,
    PlanCalculationValueInput,
  },

  props: {
    mileage: { type: [String, Number], default: null },
    telemetryMileage: { type: [String, Number], default: null },
    plannedMileage: { type: [String, Number], default: null },
    overridden: { type: Boolean, default: false },
    updatedBy: { type: String, default: "" },
    updatedAt: { type: String, default: "" },
    telemetryUpdatedAt: { type: String, default: "" },
    refreshPending: { type: Boolean, default: false },
    isSubmitting: { type: Boolean, default: false },
    editable: { type: Boolean, default: false },
  },

  emits: ["save", "reset", "refresh"],

  data() {
    return {
      selectedSource: "telemetry",
      draft: "",
    };
  },

  computed: {
    hasTelemetry() {
      return this.hasValue(this.telemetryMileage);
    },
    hasPlanMileage() {
      return this.hasValue(this.plannedMileage);
    },
    manualMode() {
      return this.selectedSource === "manual";
    },
    sourceOptions() {
      return [
        { value: "telemetry", label: "Телеметрия", disabled: !this.hasTelemetry },
        { value: "manual", label: "Вручную" },
      ];
    },
    sourceNote() {
      if (this.overridden) {
        const details = [this.updatedBy, this.formatDate(this.updatedAt)].filter(Boolean);
        return details.length ? `вручную · ${details.join(" · ")}` : "вручную";
      }
      if (!this.hasTelemetry) return "телеметрии нет";
      if (this.manualMode) return "поправьте значение телеметрии";
      return "по треку тягача";
    },
    telemetryStatus() {
      return buildTelemetryFreshnessLabel({
        formattedAt: this.formatDate(this.telemetryUpdatedAt),
        hasTelemetry: this.hasTelemetry,
        overridden: this.overridden,
        refreshPending: this.refreshPending,
      });
    },
    draftChanged() {
      return isMileageDraftChanged(this.draft, this.mileage);
    },
    showSaveAction() {
      return !this.overridden || this.draftChanged;
    },
    invalid() {
      if (this.draft === "" || this.draft === null) return true;
      const value = Number(String(this.draft).replace(/\s/g, "").replace(",", "."));
      return !Number.isFinite(value) || value < 0;
    },
    comparison() {
      return buildMileageComparison(this.mileage, this.plannedMileage);
    },
    comparisonLabel() {
      if (!this.hasValue(this.mileage) && this.hasPlanMileage) {
        return `план ${this.formatMileage(this.plannedMileage)}`;
      }
      if (this.hasValue(this.mileage) && !this.hasPlanMileage) {
        return `${this.formatMileage(this.mileage)} · план не задан`;
      }
      if (!this.hasValue(this.mileage)) return "Пробег не указан";

      return `${this.formatValue(this.mileage)} из ${this.formatMileage(this.plannedMileage)} · ${this.formatDifference(this.comparison.differencePercent)}`;
    },
    differenceTone() {
      if (this.comparison.differencePercent === null || this.comparison.differencePercent === 0) return "";
      return this.comparison.differencePercent > 0
        ? "fact-mileage__comparison-label--warning"
        : "";
    },
  },

  watch: {
    overridden() {
      this.syncSource();
    },
    telemetryMileage() {
      this.syncSource();
    },
    mileage() {
      this.syncSource();
    },
  },

  created() {
    this.syncSource();
  },

  methods: {
    syncSource() {
      this.selectedSource = this.overridden || !this.hasTelemetry
        ? "manual"
        : "telemetry";
      if (this.selectedSource === "manual") {
        this.draft = this.mileage ?? this.telemetryMileage ?? "";
      }
    },
    changeSource(source) {
      if (!this.editable) return;

      if (source === "telemetry") {
        this.selectTelemetry();
        return;
      }

      this.draft = this.mileage ?? this.telemetryMileage ?? "";
      this.focusInput();
    },
    selectTelemetry() {
      if (!this.hasTelemetry) return;
      if (this.overridden) {
        // v-model уже переключил control, но persisted override снимается только после успешного PATCH.
        this.selectedSource = "manual";
        this.$emit("reset");
        return;
      }
      this.selectedSource = "telemetry";
    },
    save() {
      if (!this.editable || this.invalid || this.isSubmitting) return;
      this.$emit("save", this.draft);
    },
    focusInput() {
      this.$nextTick(() => {
        this.$refs.input?.focus();
      });
    },
    cancelEdit() {
      this.draft = this.mileage ?? this.telemetryMileage ?? "";
      if (!this.overridden && this.hasTelemetry) this.selectedSource = "telemetry";
      this.$nextTick(() => this.$refs.input?.blur());
    },
    formatValue(value) {
      return formatBigNumber(value);
    },
    formatMileage(value) {
      return `${formatBigNumber(value)} км`;
    },
    formatDifference(value) {
      if (value === null) return "—";
      if (value > 0) return `+${value}%`;
      if (value < 0) return `−${Math.abs(value)}%`;
      return "0%";
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== "";
    },
  },
};
</script>

<style scoped>
.fact-mileage {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 2px 0;
  font-size: 13px;
}

.fact-mileage__header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 30px;
}

.fact-mileage__header > strong {
  font-weight: 600;
}

.fact-mileage__note {
  color: #8b93a7;
  font-size: 12px;
}

.fact-mileage__note--missing {
  color: #b45309;
}

.fact-mileage__field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #5b6070;
  font-size: 12px;
}

.fact-mileage__freshness {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #8b93a7;
  font-size: 11px;
  white-space: nowrap;
}

.fact-mileage__source {
  flex: none;
}

.fact-mileage__comparison {
  display: flex;
  align-items: center;
  gap: 12px;
}

.fact-mileage__bar {
  position: relative;
  height: 6px;
  flex: 1;
  border-radius: 999px;
  background: #eef1f8;
}

.fact-mileage__fill {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: 999px;
  background: #16a34a;
}

.fact-mileage__fill--manual {
  background: #f59e0b;
}

.fact-mileage__plan-marker {
  position: absolute;
  top: -3px;
  bottom: -3px;
  width: 2px;
  background: #3b5bdb;
  transform: translateX(-1px);
}

.fact-mileage__comparison-label {
  width: 170px;
  flex: none;
  color: #5b6070;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}

.fact-mileage__comparison-label--warning {
  color: #b45309;
}

@media (max-width: 680px) {
  .fact-mileage__header {
    flex-wrap: wrap;
  }
  .fact-mileage__field {
    order: 3;
    width: 100%;
  }
  .fact-mileage__freshness {
    order: 4;
    margin-left: 0;
  }
  .fact-mileage__source {
    order: 5;
    margin-left: auto;
  }
  .fact-mileage__comparison-label {
    width: auto;
  }
}
</style>
